import {
  changeEsignatureCredentialSigningCredentialChangePost,
  completeEsignatureCredentialResetSigningCredentialResetPost,
  getEsignatureCredentialStatusSigningCredentialGet,
  requestEsignatureCredentialResetSigningCredentialResetRequestPost,
  setEsignatureCredentialSigningCredentialPut,
} from '../../services/api/sdk.gen';

/**
 * Minimum e-signature password length.
 *
 * Mirrors MIN_PASSWORD_LENGTH in backend/app/services/esignature_credential.py so
 * a too-short password is caught before the request instead of coming back as a
 * 422 after the dialog has already closed.
 */
export const MIN_ESIGN_PASSWORD_LENGTH = 8;

/** Whether the signed-in user has an e-signature password, and any lockout. */
export interface ESignatureCredentialStatus {
  has_credential: boolean;
  locked_until: string | null;
}

/**
 * The e-signature password a signing request presents.
 *
 * Shaped to match the wire body so panels can spread it straight into their
 * existing sign / approve request alongside `attestation_version`. Exactly one
 * field is set: `new_esign_password` on a clinician's first-ever signature,
 * `esign_password` every time after that.
 */
export interface ESignatureCredentialInput {
  esign_password?: string;
  new_esign_password?: string;
}

export async function fetchESignatureCredentialStatus(): Promise<ESignatureCredentialStatus> {
  const { data } = await getEsignatureCredentialStatusSigningCredentialGet({
    throwOnError: true,
  });
  return {
    has_credential: Boolean(data?.has_credential),
    locked_until: data?.locked_until ?? null,
  };
}

export async function createESignatureCredential(
  newPassword: string
): Promise<void> {
  await setEsignatureCredentialSigningCredentialPut({
    body: { new_password: newPassword },
    throwOnError: true,
  });
}

export async function changeESignatureCredential(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await changeEsignatureCredentialSigningCredentialChangePost({
    body: { current_password: currentPassword, new_password: newPassword },
    throwOnError: true,
  });
}

/** Email the signed-in user a single-use code to reset their password. */
export async function requestESignatureCredentialReset(): Promise<void> {
  await requestEsignatureCredentialResetSigningCredentialResetRequestPost({
    throwOnError: true,
  });
}

export async function completeESignatureCredentialReset(
  code: string,
  newPassword: string
): Promise<void> {
  await completeEsignatureCredentialResetSigningCredentialResetPost({
    body: { code, new_password: newPassword },
    throwOnError: true,
  });
}
